import React, { useCallback, useEffect, useImperativeHandle, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Input } from '@codes/ui-kit/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@codes/ui-kit/components/ui/select';
import { Textarea } from '@codes/ui-kit/components/ui/textarea';
import { PromptBody, PromptView } from '@lemoncloud/ssocio-chatbots-api';
import { PromptState } from '@web/app/features/agents/types';
import { isTemplatePrompt } from '@web/app/features/agents/utils';

import { AgentFormData, ClonePromptDTO, CreatePromptDTO, RevisionRequestView, SelectOption } from '@codes/agents';

import {
    contentsToString,
    formatNumber,
    formatTemperatureDisplay,
    generatePromptName,
    getTemperatureDisplayValue,
} from '../utils/prompt-utils';

export interface AgentFormBasicRef {
    getPromptCreationData: () => {
        systemPrompt?: CreatePromptDTO;
        userPrompt?: CreatePromptDTO;
    };
    getPromptUpdateData: () => {
        systemPrompt?: { promptId: string; contents: string[] };
        userPrompt?: { promptId: string; contents: string[] };
    };
    getPromptTemplateData: () => {
        systemPrompt?: CreatePromptDTO;
        userPrompt?: CreatePromptDTO;
    };
}

interface FormFieldProps {
    label: string;
    children: React.ReactNode;
    required?: boolean;
}

const FormField = React.memo<FormFieldProps>(({ label, children, required = false }) => {
    const { t } = useTranslation();

    return (
        <div>
            <label className="inline-block text-base font-medium text-text mb-[9px]">
                {label}
                {required && <span className="text-error ml-1">{t('agent.form.basic.states.required')}</span>}
            </label>
            {children}
        </div>
    );
});

interface AgentFormBasicProps {
    formData: AgentFormData;
    onChange: (updates: Partial<AgentFormData>) => void;
    isLoading?: boolean;
    selectOptions: {
        systemPrompts: SelectOption[];
        userPrompts: SelectOption[];
        brains: SelectOption[];
        embeddings: SelectOption[];
    };
    promptsData: {
        systemPrompts: PromptView[];
        userPrompts: PromptView[];
    };
    onPromptClone?: (promptData: ClonePromptDTO) => Promise<RevisionRequestView<PromptView>>;
    onPromptUpdate?: (promptData: { promptId: string; contents: string[] }) => Promise<RevisionRequestView<PromptView>>;
    systemPromptState: PromptState;
    userPromptState: PromptState;
    onSystemPromptStateChange: (updates: Partial<PromptState>) => void;
    onUserPromptStateChange: (updates: Partial<PromptState>) => void;
    hasBrainsError?: boolean;
    hasEmbeddingsError?: boolean;
    systemPromptsError?: unknown;
    userPromptsError?: unknown;
    systemPromptsRefetch?: () => void;
    userPromptsRefetch?: () => void;
    isLoadingSystemPrompts?: boolean;
    isLoadingUserPrompts?: boolean;
    mode: 'create' | 'edit';
    currentSystemPrompt?: PromptView | null;
    currentUserPrompt?: PromptView | null;
}

/**
 * 프롬프트가 수정되었는지 확인하는 헬퍼 함수
 */
const isPromptModified = (promptState: PromptState): boolean => {
    return promptState.mode === 'existing' && promptState.loadedContent !== promptState.originalContent;
};

export const AgentFormBasic = React.memo(
    React.forwardRef<AgentFormBasicRef, AgentFormBasicProps>(
        (
            {
                formData,
                onChange,
                isLoading = false,
                selectOptions,
                promptsData,
                onPromptClone,
                onPromptUpdate,
                systemPromptState,
                userPromptState,
                onSystemPromptStateChange,
                onUserPromptStateChange,
                hasBrainsError = false,
                hasEmbeddingsError = false,
                systemPromptsError,
                userPromptsError,
                systemPromptsRefetch,
                userPromptsRefetch,
                isLoadingSystemPrompts,
                isLoadingUserPrompts,
                mode,
                currentSystemPrompt,
                currentUserPrompt,
            },
            ref
        ) => {
            const { t } = useTranslation();

            // Prompt content mapping
            const promptContentMap = useMemo(() => {
                const map = new Map<string, string>();

                promptsData.systemPrompts.forEach(prompt => {
                    if (prompt.id && prompt.contents) {
                        const content = contentsToString(prompt.contents);
                        const isTemplate = isTemplatePrompt(prompt);
                        const key = isTemplate ? `system:${prompt.id}` : prompt.id;
                        map.set(key, content);
                    }
                });

                promptsData.userPrompts.forEach(prompt => {
                    if (prompt.id && prompt.contents) {
                        const content = contentsToString(prompt.contents);
                        const isTemplate = isTemplatePrompt(prompt);
                        const key = isTemplate ? `user:${prompt.id}` : prompt.id;
                        map.set(key, content);
                    }
                });

                if (currentSystemPrompt && currentSystemPrompt.id && currentSystemPrompt.contents) {
                    const content = contentsToString(currentSystemPrompt.contents);
                    const isTemplate = isTemplatePrompt(currentSystemPrompt);
                    const key = isTemplate ? `system:${currentSystemPrompt.id}` : currentSystemPrompt.id;
                    map.set(key, content);
                }

                if (currentUserPrompt && currentUserPrompt.id && currentUserPrompt.contents) {
                    const content = contentsToString(currentUserPrompt.contents);
                    const isTemplate = isTemplatePrompt(currentUserPrompt);
                    const key = isTemplate ? `user:${currentUserPrompt.id}` : currentUserPrompt.id;
                    map.set(key, content);
                }

                return map;
            }, [promptsData.systemPrompts, promptsData.userPrompts, currentSystemPrompt, currentUserPrompt]);

            const enhancedSystemPrompts = useMemo(() => {
                return selectOptions.systemPrompts;
            }, [selectOptions.systemPrompts]);

            const enhancedUserPrompts = useMemo(() => {
                return selectOptions.userPrompts;
            }, [selectOptions.userPrompts]);

            const getPromptContent = useCallback(
                (promptId: string, stereo: 'system' | 'user'): string => {
                    let prompt: PromptView | undefined;

                    switch (stereo) {
                        case 'system':
                            prompt = promptsData.systemPrompts.find(p => p.id === promptId);
                            if (!prompt && currentSystemPrompt && currentSystemPrompt.id === promptId) {
                                prompt = currentSystemPrompt;
                            }
                            break;
                        case 'user':
                            prompt = promptsData.userPrompts.find(p => p.id === promptId);
                            if (!prompt && currentUserPrompt && currentUserPrompt.id === promptId) {
                                prompt = currentUserPrompt;
                            }
                            break;
                    }

                    const isTemplate = prompt ? isTemplatePrompt(prompt) : false;
                    const key = isTemplate ? `${stereo}:${promptId}` : promptId;
                    return promptContentMap.get(key) || '';
                },
                [
                    promptContentMap,
                    promptsData.systemPrompts,
                    promptsData.userPrompts,
                    currentSystemPrompt,
                    currentUserPrompt,
                ]
            );

            const handlePromptChange = useCallback(
                (field: 'promptId' | 'userPromptId', value: string) => {
                    const content = getPromptContent(value, field === 'promptId' ? 'system' : 'user');
                    const selectedPrompt =
                        field === 'promptId'
                            ? promptsData.systemPrompts.find(p => p.id === value)
                            : promptsData.userPrompts.find(p => p.id === value);

                    const isTemplate = selectedPrompt ? isTemplatePrompt(selectedPrompt) : false;

                    switch (field) {
                        case 'promptId':
                            onSystemPromptStateChange({
                                mode: 'existing',
                                loadedContent: content,
                                originalContent: content,
                                selectedPromptId: value,
                                isTemplate,
                            });
                            break;
                        case 'userPromptId':
                            onUserPromptStateChange({
                                mode: 'existing',
                                loadedContent: content,
                                originalContent: content,
                                selectedPromptId: value,
                                isTemplate,
                            });
                            break;
                    }

                    // 템플릿 프롬프트인 경우 formData에 ID를 저장하지 않음 (나중에 클론 후 설정)
                    // 일반 프롬프트인 경우 바로 ID 설정
                    onChange({ [field]: isTemplate ? '' : value });
                },
                [
                    onChange,
                    getPromptContent,
                    onSystemPromptStateChange,
                    onUserPromptStateChange,
                    promptsData.systemPrompts,
                    promptsData.userPrompts,
                ]
            );

            const handleFieldChange = useCallback(
                (field: keyof AgentFormData) => (value: string | number) => {
                    onChange({ [field]: value });
                },
                [onChange]
            );

            // Effects
            useEffect(() => {
                if (formData.promptId && !systemPromptState.loadedContent) {
                    const content = getPromptContent(formData.promptId, 'system');
                    const selectedPrompt = promptsData.systemPrompts.find(p => p.id === formData.promptId);
                    const isTemplate = selectedPrompt ? isTemplatePrompt(selectedPrompt) : false;

                    onSystemPromptStateChange({
                        loadedContent: content,
                        mode: 'existing',
                        originalContent: content,
                        selectedPromptId: formData.promptId,
                        isTemplate,
                    });

                    if (isTemplate) {
                        onChange({ promptId: '' });
                    }
                }
            }, [
                formData.promptId,
                getPromptContent,
                systemPromptState.loadedContent,
                onSystemPromptStateChange,
                promptsData.systemPrompts,
                onChange,
            ]);

            useEffect(() => {
                if (formData.userPromptId && !userPromptState.loadedContent) {
                    const content = getPromptContent(formData.userPromptId, 'user');
                    const selectedPrompt = promptsData.userPrompts.find(p => p.id === formData.userPromptId);
                    const isTemplate = selectedPrompt ? isTemplatePrompt(selectedPrompt) : false;

                    onUserPromptStateChange({
                        loadedContent: content,
                        mode: 'existing',
                        originalContent: content,
                        selectedPromptId: formData.userPromptId,
                        isTemplate,
                    });

                    if (isTemplate) {
                        onChange({ userPromptId: '' });
                    }
                }
            }, [
                formData.userPromptId,
                getPromptContent,
                userPromptState.loadedContent,
                onUserPromptStateChange,
                promptsData.userPrompts,
                onChange,
            ]);

            // Imperative handle
            useImperativeHandle(
                ref,
                () => ({
                    getPromptCreationData: () => {
                        // Direct input 기능 제거됨
                        return {};
                    },
                    getPromptUpdateData: () => {
                        // This function is now handled by getPromptTemplateData
                        return {};
                    },
                    getPromptTemplateData: () => {
                        const result: { systemPrompt?: CreatePromptDTO; userPrompt?: CreatePromptDTO } = {};

                        // 시스템 프롬프트: 수정된 기존 프롬프트만 처리 (템플릿은 AgentForm에서 직접 처리)
                        if (
                            systemPromptState.selectedPromptId &&
                            !systemPromptState.isTemplate &&
                            systemPromptState.mode === 'existing' &&
                            systemPromptState.loadedContent !== systemPromptState.originalContent
                        ) {
                            const content = systemPromptState.loadedContent || '';
                            if (content.trim()) {
                                result.systemPrompt = {
                                    name: generatePromptName(
                                        content.split('\n'),
                                        'system',
                                        t('agent.form.advanced.prompts.names.customSystem')
                                    ),
                                    contents: content.split('\n'),
                                    stereo: 'system',
                                } as PromptBody;
                            }
                        }

                        // 사용자 프롬프트: 수정된 기존 프롬프트만 처리 (템플릿은 AgentForm에서 직접 처리)
                        if (
                            userPromptState.selectedPromptId &&
                            !userPromptState.isTemplate &&
                            userPromptState.mode === 'existing' &&
                            userPromptState.loadedContent !== userPromptState.originalContent
                        ) {
                            const content = userPromptState.loadedContent || '';
                            if (content.trim()) {
                                result.userPrompt = {
                                    name: generatePromptName(
                                        content.split('\n'),
                                        'user',
                                        t('agent.form.advanced.prompts.names.customUser')
                                    ),
                                    contents: content.split('\n'),
                                    stereo: 'user',
                                } as PromptBody;
                            }
                        }

                        return result;
                    },
                }),
                [systemPromptState, userPromptState, t, formData.promptId, formData.userPromptId]
            );

            // Render functions
            const renderPromptSection = (
                title: string,
                field: 'promptId' | 'userPromptId',
                options: SelectOption[],
                placeholder: string,
                promptState: PromptState,
                error?: unknown,
                refetch?: (() => void) | undefined,
                isLoadingPrompt?: boolean
            ) => {
                const isSystem = field === 'promptId';
                // 템플릿 프롬프트인 경우 selectedPromptId를 사용하고, 그렇지 않으면 formData의 값을 사용
                const selectedValue = promptState.isTemplate
                    ? promptState.selectedPromptId || ''
                    : formData[field] || '';
                const textareaValue = promptState.loadedContent;

                const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
                    const newContent = e.target.value;

                    switch (isSystem) {
                        case true:
                            onSystemPromptStateChange({ loadedContent: newContent });
                            break;
                        case false:
                            onUserPromptStateChange({ loadedContent: newContent });
                            break;
                    }
                };

                const errorMessage: string = error
                    ? typeof error === 'string'
                        ? error
                        : (error as any)?.message
                        ? String((error as any).message)
                        : t('agent.form.advanced.prompts.errors.loadFailed')
                    : '';

                return (
                    <div>
                        <label className="inline-block text-base font-medium text-text mb-[9px]">{title}</label>
                        <Select
                            value={selectedValue}
                            onValueChange={value => handlePromptChange(field, value)}
                            disabled={isLoading}
                        >
                            <SelectTrigger className="h-[45px] border-placeholder mb-2">
                                <SelectValue placeholder={placeholder} />
                            </SelectTrigger>
                            <SelectContent>
                                {options.map(option => (
                                    <SelectItem key={option.value} value={option.value}>
                                        {option.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>

                        {Boolean(errorMessage) && (
                            <div className="flex items-center gap-2 text-xs text-error mb-2">
                                <span>{errorMessage}</span>
                                {refetch && (
                                    <button
                                        type="button"
                                        className="px-2 py-1 border rounded text-xs border-error text-error hover:bg-error/10 disabled:opacity-50"
                                        onClick={refetch}
                                        disabled={!!isLoadingPrompt}
                                    >
                                        {isLoadingPrompt
                                            ? t('agent.form.advanced.prompts.errors.loading')
                                            : t('agent.form.advanced.prompts.errors.retry')}
                                    </button>
                                )}
                            </div>
                        )}

                        {promptState.mode === 'existing' && (
                            <div className="text-xs text-text-500 mb-2">
                                {promptState.isTemplate
                                    ? t('agent.form.advanced.prompts.descriptions.templateMode')
                                    : t('agent.form.advanced.prompts.descriptions.existingMode')}{' '}
                                - {t('agent.form.advanced.prompts.descriptions.editable')}
                                {isPromptModified(promptState) && (
                                    <span className="text-link ml-1">
                                        {promptState.isTemplate
                                            ? t('agent.form.advanced.prompts.descriptions.willCreateNew')
                                            : t('agent.form.advanced.prompts.descriptions.modified')}
                                    </span>
                                )}
                            </div>
                        )}

                        <Textarea
                            className={`${isSystem ? 'h-[300px]' : 'h-[260px]'}`}
                            placeholder={t('agent.form.advanced.prompts.placeholders.emptyPrompt')}
                            value={textareaValue}
                            onChange={handleTextareaChange}
                            disabled={isLoading}
                        />

                        {!isSystem && (
                            <div className="text-link font-medium mt-[6px] text-sm">
                                {t('agent.form.advanced.prompts.descriptions.userPromptNote')}
                            </div>
                        )}
                    </div>
                );
            };

            const renderSettingsSection = () => (
                <div>
                    <div className="h-px my-[10px] w-full bg-detail-line md:hidden"></div>
                    <div className="grid grid-cols-2 gap-4 max-xs:grid-cols-1">
                        <div>
                            <label className="inline-block text-base font-medium text-text mb-[5px]">
                                {t('agent.form.advanced.settings.temperature.label')}
                            </label>
                            <div className="text-sm text-text-500 mb-4">
                                {t('agent.form.advanced.settings.temperature.description')}
                            </div>
                            <div className="w-full px-1.5">
                                <div className="relative">
                                    <input
                                        type="range"
                                        min={0}
                                        max={100}
                                        step={1}
                                        value={formData.temperature ?? 100}
                                        onChange={e => {
                                            const apiValue = Number(e.target.value);
                                            handleFieldChange('temperature')(apiValue);
                                        }}
                                        disabled={isLoading}
                                        className="w-full h-1 appearance-none rounded-full outline-none slider-thumb"
                                        style={{
                                            background: `linear-gradient(
                        to right,
                        hsl(var(--point)) 0%,
                        hsl(var(--point)) ${((formData.temperature ?? 100) / 100) * 100}%,
                        hsl(var(--text-400)) ${((formData.temperature ?? 100) / 100) * 100}%,
                        hsl(var(--text-400)) 100%
                    )`,
                                        }}
                                    />
                                    <div
                                        className="absolute -bottom-6 text-sm font-medium text-point transform whitespace-nowrap"
                                        style={{
                                            left: `${((formData.temperature ?? 100) / 100) * 100}%`,
                                            transform:
                                                (formData.temperature ?? 100) === 100
                                                    ? 'translateX(-100%)'
                                                    : 'translateX(-50%)',
                                        }}
                                    >
                                        {formatTemperatureDisplay(getTemperatureDisplayValue(formData.temperature), t)}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div>
                            <label className="inline-block text-base font-medium text-text mb-[5px]">
                                {t('agent.form.advanced.settings.maxTokens.label')}
                            </label>
                            <div className="text-sm text-text-500 mb-4">
                                {t('agent.form.advanced.settings.maxTokens.description')}
                            </div>
                            <div className="w-full px-1.5">
                                <div className="relative">
                                    <input
                                        type="range"
                                        min={0}
                                        max={4096}
                                        step={1}
                                        value={formData.maxTokens ?? 2048}
                                        onChange={e => handleFieldChange('maxTokens')(Number(e.target.value))}
                                        disabled={isLoading}
                                        className="w-full h-1 appearance-none rounded-full outline-none slider-thumb"
                                        style={{
                                            background: `linear-gradient(
                        to right,
                        hsl(var(--point)) 0%,
                        hsl(var(--point)) ${((formData.maxTokens ?? 0) / 4096) * 100}%,
                        hsl(var(--text-400)) ${((formData.maxTokens ?? 0) / 4096) * 100}%,
                        hsl(var(--text-400)) 100%
                    )`,
                                        }}
                                    />
                                    <div
                                        className="absolute -bottom-6 text-sm font-medium text-point transform whitespace-nowrap"
                                        style={{
                                            left: `${((formData.maxTokens ?? 0) / 4096) * 100}%`,
                                            transform:
                                                (formData.maxTokens ?? 0) === 4096
                                                    ? 'translateX(-100%)'
                                                    : 'translateX(-50%)',
                                        }}
                                    >
                                        {(formData.maxTokens ?? 0) === 4096
                                            ? `${t('agent.form.advanced.settings.maxTokens.maxLabel')} `
                                            : ''}
                                        {formatNumber(formData.maxTokens ?? 0)}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            );

            return (
                <div className="space-y-[38px] h-full overflow-auto p-4 pb-20 max-md:space-y-4">
                    {/* Name Field */}
                    <FormField label={t('agent.form.basic.fields.name')} required>
                        <Input
                            placeholder={t('agent.form.basic.placeholders.name')}
                            className="h-[45px] border-placeholder"
                            value={formData.nick || ''}
                            onChange={e => onChange({ nick: e.target.value })}
                            disabled={isLoading}
                            maxLength={50}
                        />
                    </FormField>

                    {/* Model and Embedding Fields */}
                    <div className="grid grid-cols-2 gap-4 max-md:grid-cols-1">
                        <FormField label={t('agent.form.basic.fields.model')} required>
                            <Select
                                value={formData.brainId || ''}
                                onValueChange={value => handleFieldChange('brainId')(value)}
                                disabled={isLoading || selectOptions.brains.length === 0 || hasBrainsError}
                            >
                                <SelectTrigger className="h-[45px] border-placeholder">
                                    <SelectValue
                                        placeholder={
                                            hasBrainsError
                                                ? t('agent.form.basic.states.loadError')
                                                : selectOptions.brains.length === 0
                                                ? t('agent.form.basic.states.noData')
                                                : t('agent.form.basic.placeholders.model')
                                        }
                                    />
                                </SelectTrigger>
                                <SelectContent>
                                    {hasBrainsError ? (
                                        <SelectItem value="__error__" disabled>
                                            {t('agent.form.basic.states.loadError', 'Load Error')}
                                        </SelectItem>
                                    ) : (
                                        selectOptions.brains.map(option => (
                                            <SelectItem key={option.value} value={option.value}>
                                                {option.label}
                                            </SelectItem>
                                        ))
                                    )}
                                </SelectContent>
                            </Select>
                        </FormField>

                        <FormField label={t('agent.form.basic.fields.embedding')} required>
                            <Select
                                value={formData.embedding || ''}
                                onValueChange={value => handleFieldChange('embedding')(value)}
                                disabled={isLoading || selectOptions.embeddings.length === 0}
                            >
                                <SelectTrigger className="h-[45px] border-placeholder">
                                    <SelectValue
                                        placeholder={
                                            selectOptions.embeddings.length === 0
                                                ? t('agent.form.basic.states.noData')
                                                : t('agent.form.basic.placeholders.embedding')
                                        }
                                    />
                                </SelectTrigger>
                                <SelectContent>
                                    {hasEmbeddingsError ? (
                                        <SelectItem value="__error__" disabled>
                                            {t('agent.form.basic.states.loadError', 'Load Error')}
                                        </SelectItem>
                                    ) : (
                                        selectOptions.embeddings.map(option => (
                                            <SelectItem key={option.value} value={option.value}>
                                                {option.label}
                                            </SelectItem>
                                        ))
                                    )}
                                </SelectContent>
                            </Select>
                        </FormField>
                    </div>

                    {renderPromptSection(
                        t('agent.form.advanced.prompts.systemPrompt'),
                        'promptId',
                        enhancedSystemPrompts,
                        t('agent.form.advanced.prompts.placeholders.selectSystemPrompt'),
                        systemPromptState,
                        systemPromptsError,
                        systemPromptsRefetch,
                        isLoadingSystemPrompts
                    )}

                    {renderPromptSection(
                        t('agent.form.advanced.prompts.userPrompt'),
                        'userPromptId',
                        enhancedUserPrompts,
                        t('agent.form.advanced.prompts.placeholders.selectUserPrompt'),
                        userPromptState,
                        userPromptsError,
                        userPromptsRefetch,
                        isLoadingUserPrompts
                    )}

                    {renderSettingsSection()}
                </div>
            );
        }
    )
);

AgentFormBasic.displayName = 'AgentFormBasic';
